import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { apiErrorDetail, fraudClient } from '@/lib/api/fraud-client'
import { useAcceptDispute, useDispute, useRedraftDispute, useSubmitDispute, useUpdateDispute } from '@/lib/api/fraud-queries'
import type { DisputeDraft, DisputeRecommendation, DisputeReversal } from '@/lib/api/fraud-types'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { EMPTY, PILL, fmtDateTime, moneyFormatter } from '../format'
import { Banner, FinixLink, Spinner } from '../shared'
import { RECOMMENDATION_META, daysUntil, responseStatus } from './dispute-response'

const BUTTON = 'rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed'
const PRIMARY = cn(BUTTON, 'bg-blue-600 text-white hover:bg-blue-700')
const SECONDARY = cn(BUTTON, 'border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800')
const DANGER = cn(BUTTON, 'bg-red-600 text-white hover:bg-red-700')

type Confirming = null | 'submit' | 'accept'

/**
 * One Finix dispute's drafted response: the recommendation and why, the
 * evidence PDF, the note, and the two one-shot answers (submit the
 * evidence, or accept liability). Answers that the rules flagged
 * (no ledger match, needs review) need an explicit override.
 */
export function DisputeReviewModal({ chargebackId, onClose }: { chargebackId: number; onClose: () => void }) {
  const { data: dispute, isLoading, error } = useDispute(chargebackId)

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEscape)
    document.body.classList.add('overflow-hidden')
    return () => {
      document.removeEventListener('keydown', onEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Dispute response"
        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 p-4 dark:border-neutral-800">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Dispute response</h2>
            {dispute && <DisputeSummary dispute={dispute} />}
          </div>
          <button onClick={onClose} aria-label="Close" className="rounded-md p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {error ? (
            <Banner kind="error">{apiErrorDetail(error, 'Could not load the dispute.')}</Banner>
          ) : isLoading || !dispute ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : (
            <DisputeBody dispute={dispute} />
          )}
        </div>
      </div>
    </div>
  )
}

function DisputeSummary({ dispute }: { dispute: DisputeDraft }) {
  const money = moneyFormatter(dispute.currency)
  const status = responseStatus(dispute)
  const days = dispute.provider_response_state === 'NEEDS_RESPONSE' ? daysUntil(dispute.respond_by) : null
  return (
    <div className="mt-1 space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-neutral-900 dark:text-white">{money(dispute.amount)}</span>
        <span>{[dispute.reason, dispute.reason_code].filter(Boolean).join(' · ') || EMPTY}</span>
        {status && <span className={cn(PILL, status.chip)} title={status.title}>{status.label}</span>}
        {dispute.high_priority && <span className={cn(PILL, 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300')} title="This charge was 3-D Secure authenticated">3DS</span>}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <span>{dispute.event_name ?? EMPTY}</span>
        <span>{dispute.user_name || 'No matching charge'}</span>
        <span>
          Respond by {fmtDateTime(dispute.respond_by)}
          {days !== null && <span className={cn('ml-1', days <= 3 && 'font-medium text-red-600 dark:text-red-400')}>({days < 0 ? 'overdue' : `${days}d left`})</span>}
        </span>
        <FinixLink id={dispute.provider_dispute_id} kind="dispute" />
      </div>
    </div>
  )
}

function DisputeBody({ dispute }: { dispute: DisputeDraft }) {
  const answered = !!dispute.response_action || !!dispute.submitted_at
  return (
    <div className="space-y-5">
      {answered ? <AnsweredSection dispute={dispute} /> : <PendingSection dispute={dispute} />}
      <EvidenceSection dispute={dispute} />
    </div>
  )
}

function AnsweredSection({ dispute }: { dispute: DisputeDraft }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">What we sent</h3>
      <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-neutral-500">Answer</dt>
        <dd>{dispute.response_action === 'accepted' || dispute.response_action === 'accepting' ? 'Accepted liability' : 'Submitted evidence'}</dd>
        <dt className="text-neutral-500">Sent</dt><dd>{fmtDateTime(dispute.submitted_at)}</dd>
        <dt className="text-neutral-500">Evidence uploaded</dt><dd>{fmtDateTime(dispute.evidence_uploaded_at)}</dd>
        {dispute.reversed_at && (
          <>
            <dt className="text-neutral-500">Tokens taken back</dt>
            <dd>{dispute.reversed_tokens}{dispute.unreversed_tokens ? ` (${dispute.unreversed_tokens} already spent)` : ''}</dd>
          </>
        )}
      </dl>
      {dispute.response_note && (
        <p className="whitespace-pre-wrap rounded-md bg-neutral-50 p-3 text-sm dark:bg-neutral-800/50">{dispute.response_note}</p>
      )}
    </section>
  )
}

function PendingSection({ dispute }: { dispute: DisputeDraft }) {
  const update = useUpdateDispute()
  const redraft = useRedraftDispute()
  // Staff's unsaved edit, else the saved note (which a redraft may replace).
  const [edited, setEdited] = useState<string | null>(null)
  const note = edited ?? dispute.response_note
  const [message, setMessage] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null)
  const [confirming, setConfirming] = useState<Confirming>(null)

  const run = async (action: () => Promise<unknown>, success: string, fallback: string) => {
    setMessage(null)
    try {
      await action()
      setMessage({ kind: 'success', text: success })
    } catch (err) {
      setMessage({ kind: 'error', text: apiErrorDetail(err, fallback) })
    }
  }

  if (!dispute.has_evidence) {
    return (
      <section className="space-y-3">
        {message && <Banner kind={message.kind}>{message.text}</Banner>}
        <p className="text-sm text-neutral-600 dark:text-neutral-400">No evidence has been drafted for this dispute yet.</p>
        <button
          className={PRIMARY}
          disabled={redraft.isPending}
          onClick={() => run(() => redraft.mutateAsync({ chargebackId: dispute.id, data: {} }), 'Evidence drafted.', 'Could not draft the evidence.')}
        >
          {redraft.isPending ? 'Drafting…' : 'Draft evidence'}
        </button>
      </section>
    )
  }

  const recommendation = dispute.recommendation || dispute.computed_recommendation
  const noteDirty = note !== dispute.response_note
  return (
    <section className="space-y-4">
      {message && <Banner kind={message.kind}>{message.text}</Banner>}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-neutral-900 dark:text-white">Recommendation</span>
          <select
            value={recommendation}
            disabled={update.isPending}
            onChange={(e) => run(
              () => update.mutateAsync({ chargebackId: dispute.id, data: { recommendation: e.target.value as DisputeRecommendation } }),
              'Recommendation updated.', 'Could not update the recommendation.',
            )}
            className={cn(PILL, 'cursor-pointer border-0', recommendation ? RECOMMENDATION_META[recommendation].chip : '')}
          >
            {(Object.keys(RECOMMENDATION_META) as DisputeRecommendation[]).map((r) => (
              <option key={r} value={r}>{RECOMMENDATION_META[r].label}</option>
            ))}
          </select>
          {dispute.recommendation_overridden && dispute.computed_recommendation && (
            <span className="text-xs text-neutral-500">Changed by staff; the draft said {RECOMMENDATION_META[dispute.computed_recommendation].label.toLowerCase()}.</span>
          )}
        </div>
        {dispute.recommendation_reasons.length > 0 && (
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-neutral-700 dark:text-neutral-300">
            {dispute.recommendation_reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        )}
        {dispute.balance_now !== null && (
          <p className="text-xs text-neutral-500">Live balance at the event: {dispute.balance_now} tokens.</p>
        )}
      </div>

      {dispute.blockers.length > 0 && (
        <Banner kind="info">
          <span className="font-medium">Needs a confirmation to answer: </span>{dispute.blockers.join(' ')}
        </Banner>
      )}

      <div className="space-y-2">
        <label htmlFor="dispute-note" className="text-sm font-semibold text-neutral-900 dark:text-white">Note sent with the evidence</label>
        <textarea
          id="dispute-note"
          value={note}
          onChange={(e) => setEdited(e.target.value)}
          rows={6}
          maxLength={1000}
          className="w-full rounded-md border border-neutral-300 bg-white p-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={SECONDARY}
            disabled={!noteDirty || update.isPending}
            onClick={() => run(async () => {
              await update.mutateAsync({ chargebackId: dispute.id, data: { response_note: note } })
              setEdited(null)
            }, 'Note saved.', 'Could not save the note.')}
          >
            Save note
          </button>
          <button
            className={SECONDARY}
            disabled={redraft.isPending || !!dispute.evidence_uploaded_at}
            title={dispute.evidence_uploaded_at ? 'The evidence was already uploaded to Finix' : 'Re-gather the facts and redraw the PDF; your note and recommendation are kept'}
            onClick={() => run(() => redraft.mutateAsync({ chargebackId: dispute.id, data: {} }), 'Evidence redrafted.', 'Could not redraft the evidence.')}
          >
            {redraft.isPending ? 'Redrafting…' : 'Redraft evidence'}
          </button>
          <span className="ml-auto text-xs text-neutral-500">{note.length}/1000 · drafted {fmtDateTime(dispute.evidence_built_at)}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
        <button className={PRIMARY} disabled={!!confirming || noteDirty} onClick={() => setConfirming('submit')}
          title={noteDirty ? 'Save the note first' : undefined}>
          Submit evidence…
        </button>
        <button className={SECONDARY} disabled={!!confirming} onClick={() => setConfirming('accept')}>
          Accept liability…
        </button>
      </div>

      {confirming === 'submit' && (
        <SubmitConfirm dispute={dispute} onDone={(text, kind) => { setConfirming(null); if (text) setMessage({ kind, text }) }} />
      )}
      {confirming === 'accept' && (
        <AcceptConfirm dispute={dispute} onDone={(text, kind) => { setConfirming(null); if (text) setMessage({ kind, text }) }} />
      )}
    </section>
  )
}

type Done = (text: string | null, kind: 'success' | 'error' | 'info') => void

function OverrideCheckbox({ dispute, checked, onChange }: { dispute: DisputeDraft; checked: boolean; onChange: (v: boolean) => void }) {
  if (dispute.blockers.length === 0) return null
  return (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
      <span>I have reviewed this dispute and want to answer it anyway.</span>
    </label>
  )
}

function SubmitConfirm({ dispute, onDone }: { dispute: DisputeDraft; onDone: Done }) {
  const submit = useSubmitDispute()
  const [override, setOverride] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const blocked = dispute.blockers.length > 0 && !override

  const confirm = async () => {
    setError(null)
    try {
      await submit.mutateAsync({ chargebackId: dispute.id, data: { override: override || undefined } })
      onDone('Evidence submitted to Finix.', 'success')
    } catch (err) {
      if ((err as { status?: number }).status === 409) {
        onDone('Finix is still processing the uploaded evidence. Nothing was submitted; try again in a few minutes.', 'info')
        return
      }
      setError(apiErrorDetail(err, 'Could not submit the evidence.'))
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/30">
      <p className="text-sm">
        Upload the evidence PDF and submit it to the issuer with the note above. <strong>This can only be done once.</strong>
      </p>
      {error && <Banner kind="error">{error}</Banner>}
      <OverrideCheckbox dispute={dispute} checked={override} onChange={setOverride} />
      <div className="flex gap-2">
        <button className={PRIMARY} disabled={blocked || submit.isPending} onClick={confirm}>
          {submit.isPending ? 'Submitting…' : 'Submit to Finix'}
        </button>
        <button className={SECONDARY} disabled={submit.isPending} onClick={() => onDone(null, 'info')}>Cancel</button>
      </div>
    </div>
  )
}

function AcceptConfirm({ dispute, onDone }: { dispute: DisputeDraft; onDone: Done }) {
  const accept = useAcceptDispute()
  // The drafted note is a rebuttal unless the draft recommended accepting.
  const [note, setNote] = useState(dispute.computed_recommendation === 'accept' ? dispute.response_note : '')
  const [override, setOverride] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const blocked = (dispute.blockers.length > 0 && !override) || !note.trim()

  const confirm = async () => {
    setError(null)
    try {
      const result = await accept.mutateAsync({ chargebackId: dispute.id, data: { note, override: override || undefined } })
      onDone(reversalText(result.reversal), 'success')
    } catch (err) {
      setError(apiErrorDetail(err, 'Could not accept the dispute.'))
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
      <p className="text-sm">
        Accept liability at Finix. What is left of the disputed reload is taken back
        {dispute.balance_now !== null ? ` (live balance ${dispute.balance_now} tokens)` : ''} and the account is blocked.
        {' '}<strong>This can only be done once.</strong>
      </p>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="Note to Finix (required)"
        className="w-full rounded-md border border-neutral-300 bg-white p-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
      />
      {error && <Banner kind="error">{error}</Banner>}
      <OverrideCheckbox dispute={dispute} checked={override} onChange={setOverride} />
      <div className="flex gap-2">
        <button className={DANGER} disabled={blocked || accept.isPending} onClick={confirm}>
          {accept.isPending ? 'Accepting…' : 'Accept liability'}
        </button>
        <button className={SECONDARY} disabled={accept.isPending} onClick={() => onDone(null, 'info')}>Cancel</button>
      </div>
    </div>
  )
}

function reversalText(reversal: DisputeReversal | undefined): string {
  if (!reversal) return 'Dispute accepted.'
  const back = `Dispute accepted; ${reversal.reversed_tokens} tokens taken back.`
  return reversal.note ? `${back} ${reversal.note}` : back
}

/** The evidence PDF, fetched with the session's token and shown inline. */
function EvidenceSection({ dispute }: { dispute: DisputeDraft }) {
  const { token } = useAuth()
  const builtAt = dispute.evidence_built_at
  // Keyed on the draft time: a redraft fetches the new PDF.
  const pdf = useQuery({
    queryKey: ['fraud-admin', 'dispute-pdf', dispute.id, builtAt],
    queryFn: () => fraudClient.getDisputeEvidence(token!, dispute.id),
    enabled: !!token && !!builtAt,
    staleTime: Infinity,
  })
  const url = useMemo(() => (pdf.data ? URL.createObjectURL(pdf.data) : null), [pdf.data])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])

  if (!builtAt) return null
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Evidence</h3>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
            Open PDF in a new tab
          </a>
        )}
      </div>
      {pdf.error ? (
        <Banner kind="error">{apiErrorDetail(pdf.error, 'Could not load the evidence PDF.')}</Banner>
      ) : url ? (
        <iframe title="Evidence PDF" src={url} className="h-[32rem] w-full rounded-md border border-neutral-200 dark:border-neutral-800" />
      ) : (
        <div className="flex justify-center py-8"><Spinner /></div>
      )}
    </section>
  )
}
